/**
 * Servicio para manejar la API de fechas especiales
 * Proporciona funciones para gestionar fechas con horarios especiales o promociones
 */

import apiClient from './apiClient';
import { API_ENDPOINTS } from '@/config/api';

/**
 * Obtiene todas las fechas especiales
 * @returns {Promise<Array>} - Lista de fechas especiales
 */
export const getFechasEspeciales = async () => {
  try {
    //console.log('📅 [FechasEspecialesService] Obteniendo lista de fechas especiales...');
    
    const response = await apiClient.get(API_ENDPOINTS.FECHAS_ESPECIALES.BASE);
    
    if (response.data) {
      const fechasData = response.data.data || response.data.fechas_especiales || response.data;
      //console.log('✅ [FechasEspecialesService] Fechas especiales obtenidas:', fechasData.length);
      return fechasData;
    } else {
      console.warn('⚠️ [FechasEspecialesService] No se encontraron fechas especiales');
      return [];
    }
  } catch (error) {
    console.error('❌ [FechasEspecialesService] Error al obtener fechas especiales:', error);
    throw error;
  }
};

/**
 * Obtiene una fecha especial por ID
 * @param {number} fechaEspecialId - ID de la fecha especial
 * @returns {Promise<Object>} - Datos de la fecha especial
 */
export const getFechaEspecialById = async (fechaEspecialId) => {
  try {
    //console.log('📅 [FechasEspecialesService] Obteniendo fecha especial por ID:', fechaEspecialId);
    
    const response = await apiClient.get(API_ENDPOINTS.FECHAS_ESPECIALES.BY_ID(fechaEspecialId));
    
    if (response.data) {
      const fechaData = response.data.data || response.data.fecha_especial || response.data;
      //console.log('✅ [FechasEspecialesService] Fecha especial obtenida:', fechaData);
      return fechaData;
    } else {
      throw new Error('Fecha especial no encontrada');
    }
  } catch (error) {
    console.error('❌ [FechasEspecialesService] Error al obtener fecha especial:', error);
    throw error;
  }
};

/**
 * Obtiene fechas especiales por sucursal
 * @param {number} sucursalId - ID de la sucursal
 * @returns {Promise<Array>} - Lista de fechas especiales de la sucursal
 */
export const getFechasEspecialesBySucursal = async (sucursalId) => {
  try {
    //console.log('📅 [FechasEspecialesService] Obteniendo fechas especiales por sucursal:', sucursalId);
    
    const response = await apiClient.get(API_ENDPOINTS.FECHAS_ESPECIALES.BY_SUCURSAL(sucursalId));
    
    if (response.data) {
      const fechasData = response.data.data || response.data.fechas_especiales || response.data;
      //console.log('✅ [FechasEspecialesService] Fechas especiales de sucursal obtenidas:', fechasData.length);
      return fechasData;
    } else {
      return [];
    }
  } catch (error) {
    console.error('❌ [FechasEspecialesService] Error al obtener fechas especiales por sucursal:', error);
    throw error;
  }
};

/**
 * Obtiene fechas especiales en un rango de fechas
 * @param {string} fechaInicio - Fecha de inicio (YYYY-MM-DD)
 * @param {string} fechaFin - Fecha de fin (YYYY-MM-DD)
 * @param {number} sucursalId - ID de la sucursal (opcional)
 * @returns {Promise<Array>} - Lista de fechas especiales en el rango
 */
export const getFechasEspecialesByDateRange = async (fechaInicio, fechaFin, sucursalId = null) => {
  try {
    /*console.log('📅 [FechasEspecialesService] Obteniendo fechas especiales por rango:', { 
      fechaInicio, 
      fechaFin, 
      sucursalId 
    });*/
    
    const params = {
      fecha_inicio: fechaInicio,
      fecha_fin: fechaFin
    };
    
    if (sucursalId) {
      params.sucursal_id = sucursalId;
    }
    
    const response = await apiClient.get(API_ENDPOINTS.FECHAS_ESPECIALES.BY_DATE_RANGE, {
      params
    });
    
    if (response.data) {
      const fechasData = response.data.data || response.data.fechas_especiales || response.data;
      //console.log('✅ [FechasEspecialesService] Fechas especiales en rango obtenidas:', fechasData.length);
      return fechasData;
    } else {
      return [];
    }
  } catch (error) {
    console.error('❌ [FechasEspecialesService] Error al obtener fechas especiales por rango:', error);
    throw error;
  }
};

/**
 * Crea una nueva fecha especial
 * @param {Object} fechaEspecialData - Datos de la fecha especial
 * @returns {Promise<Object>} - Fecha especial creada
 */
export const createFechaEspecial = async (fechaEspecialData) => {
  try {
    //console.log('📅 [FechasEspecialesService] Creando fecha especial:', fechaEspecialData);
    
    const response = await apiClient.post(API_ENDPOINTS.FECHAS_ESPECIALES.BASE, fechaEspecialData);
    
    if (response.data) {
      const fechaEspecial = response.data.data || response.data.fecha_especial || response.data;
      //console.log('✅ [FechasEspecialesService] Fecha especial creada:', fechaEspecial);
      return fechaEspecial;
    } else {
      throw new Error('Error al crear la fecha especial');
    }
  } catch (error) {
    console.error('❌ [FechasEspecialesService] Error al crear fecha especial:', error);
    throw error;
  }
};

/**
 * Actualiza una fecha especial existente
 * @param {number} fechaEspecialId - ID de la fecha especial
 * @param {Object} fechaEspecialData - Datos actualizados de la fecha especial
 * @returns {Promise<Object>} - Fecha especial actualizada
 */
export const updateFechaEspecial = async (fechaEspecialId, fechaEspecialData) => {
  try {
    /*console.log('📅 [FechasEspecialesService] Actualizando fecha especial:', { 
      fechaEspecialId, 
      fechaEspecialData 
    });*/
    
    const response = await apiClient.put(
      API_ENDPOINTS.FECHAS_ESPECIALES.BY_ID(fechaEspecialId), 
      fechaEspecialData
    );
    
    if (response.data) {
      const fechaEspecial = response.data.data || response.data.fecha_especial || response.data;
      //console.log('✅ [FechasEspecialesService] Fecha especial actualizada:', fechaEspecial);
      return fechaEspecial;
    } else {
      throw new Error('Error al actualizar la fecha especial');
    }
  } catch (error) {
    console.error('❌ [FechasEspecialesService] Error al actualizar fecha especial:', error);
    throw error;
  }
};

/**
 * Elimina una fecha especial
 * @param {number} fechaEspecialId - ID de la fecha especial
 * @returns {Promise<boolean>} - true si se eliminó correctamente
 */
export const deleteFechaEspecial = async (fechaEspecialId) => {
  try {
    //console.log('📅 [FechasEspecialesService] Eliminando fecha especial:', fechaEspecialId);
    
    const response = await apiClient.delete(API_ENDPOINTS.FECHAS_ESPECIALES.BY_ID(fechaEspecialId));
    
    //console.log('✅ [FechasEspecialesService] Fecha especial eliminada correctamente');
    return true;
  } catch (error) {
    console.error('❌ [FechasEspecialesService] Error al eliminar fecha especial:', error);
    throw error;
  }
};

/**
 * Verifica si una fecha es especial
 * @param {string} fecha - Fecha a verificar (YYYY-MM-DD)
 * @param {number} sucursalId - ID de la sucursal (opcional)
 * @returns {Promise<boolean>} - true si es fecha especial
 */
export const isFechaEspecial = async (fecha, sucursalId = null) => {
  try {
    const fechasEspeciales = await getFechasEspecialesByDateRange(fecha, fecha, sucursalId);
    return fechasEspeciales.length > 0;
  } catch (error) {
    console.error('❌ [FechasEspecialesService] Error al verificar fecha especial:', error);
    return false;
  }
};

/**
 * Obtiene información de fecha especial para una fecha específica
 * @param {string} fecha - Fecha a consultar (YYYY-MM-DD)
 * @param {number} sucursalId - ID de la sucursal (opcional)
 * @returns {Promise<Object|null>} - Información de la fecha especial o null si no es especial
 */
export const getFechaEspecialByFecha = async (fecha, sucursalId = null) => {
  try {
    const fechasEspeciales = await getFechasEspecialesByDateRange(fecha, fecha, sucursalId);
    return fechasEspeciales.length > 0 ? fechasEspeciales[0] : null;
  } catch (error) {
    console.error('❌ [FechasEspecialesService] Error al obtener fecha especial por fecha:', error);
    return null;
  }
};

/**
 * Obtiene horarios especiales para una fecha
 * @param {string} fecha - Fecha a consultar (YYYY-MM-DD)
 * @param {number} sucursalId - ID de la sucursal (opcional)
 * @returns {Promise<Object|null>} - Horarios especiales o null si no hay
 */
export const getHorariosEspeciales = async (fecha, sucursalId = null) => {
  try {
    const fechaEspecial = await getFechaEspecialByFecha(fecha, sucursalId);
    
    if (fechaEspecial && fechaEspecial.horarios_especiales) {
      return {
        hora_apertura: fechaEspecial.hora_apertura,
        hora_cierre: fechaEspecial.hora_cierre,
        horarios_bloqueados: fechaEspecial.horarios_bloqueados || [],
        descripcion: fechaEspecial.descripcion,
        tipo: fechaEspecial.tipo
      };
    }
    
    return null;
  } catch (error) {
    console.error('❌ [FechasEspecialesService] Error al obtener horarios especiales:', error);
    return null;
  }
};